import styled from 'styled-components';
import { FiberManualRecord as FiberManualRecordIcon } from '@mui/icons-material';
import { Select, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Styling, Colors } from '../../../constants';
import { statusToColorMap } from '../../Shared/LiveEvents/statusToColorMap';
import { StreamStatus } from '../../../enums';
import VaultButton from '../../Shared/VaultButton';
import StyledSelect from '../../Shared/Select';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  min-width: 1090px;
  flex: 1 1 0;
  overflow: hidden;
  .MuiTable-root {
    table-layout: fixed;
    .MuiTableCell-root {
      padding: 6px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .MuiTableCell-root {
      &.title { width: 29%; }
      &.stream { width: 22%; }
      &.security-profile { width: 16%; }
      &.start-time { width: 11%; }
      &.duration { width: 6%; }
      &.status { width: 7%; }
      &.clip { width: 4%; }
      &.options { width: 5%; }
    }
  }
`;

export const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
`;

export const Header = styled.div`
  width: 100%;
  flex-grow: 0;
  background-color: #f9f8f8;
  padding: ${Styling.DEFAULT_SPACING}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
export const HeaderButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;
export const HeaderTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const HeaderTitle = styled.h2`
  color: black;
  font-size: 18px;
  margin-right: ${Styling.DEFAULT_SPACING}px;
`;

export const TotalCount = styled.span`
  font-size: 13px;
  color: ${Colors.BLACK_50};
  font-weight: 400;
`;

export const InfoContainer = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;

export const NoItemsText = styled.span`
  font-size: 28px;
  color: ${Colors.BLACK_50};
  padding: ${Styling.DEFAULT_SPACING}px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const TableScrollContainer = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0 10px;
`;

export const PageStatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  background-color: white;
  border-top: 1px solid ${Colors.BLACK_10};
  padding: ${Styling.DEFAULT_SPACING_HALVED}px ${Styling.DEFAULT_SPACING}px;
  min-height: 48px;
`;

export const PaginationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: ${Colors.BLACK_50};
`;

export const PageSizeSelect = styled(Select)`
  height: 28px;
  font-size: 13px;
  .MuiSelect-select {
    padding: 4px 8px;
  }
`;

export const TextContainer = styled.span`
  color: black;
  font-size: 14px;
`;

interface IStatusIconProps {
  status: StreamStatus;
}

export const StatusIcon = styled(FiberManualRecordIcon) <IStatusIconProps>`
  color: ${(props) => (statusToColorMap.get(props.status))};
  margin-right: ${Styling.DEFAULT_SPACING_HALVED}px;
`;

export const NavLinkContainer = styled.div`
  width: 40px;
`;

export const StyledClipperButton = styled(VaultButton)`
  border-radius: 50%;
  &:not([disabled]):hover {
    background-color: ${Colors.BLACK_05};
  }
`;

export const RefreshButton = styled(VaultButton)`
  padding: 3px;
  margin-right: ${Styling.DEFAULT_SPACING}px;
`;

export const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 14px;
  font-weight: 400;
  margin-right: 8px;
  white-space: nowrap;
`;

export const FilterRow = styled.tr`
  background-color: ${Colors.BLACK_02 ?? '#fafafa'};
  .MuiTableCell-root {
    padding: 4px 6px;
    border-bottom: 2px solid ${Colors.BLACK_10 ?? '#e0e0e0'};
    vertical-align: middle;
  }
`;

const FILTER_HEIGHT = '32px';
const FILTER_FONT_SIZE = '13px';

export const ColumnFilterTextField = styled(TextField)`
  width: 100%;
  .MuiInputBase-root {
    height: ${FILTER_HEIGHT};
    font-size: ${FILTER_FONT_SIZE};
    background-color: white;
  }
  .MuiInputBase-input {
    padding: 4px 8px;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${Colors.BLACK_10 ?? '#e0e0e0'};
  }
`;

export const ColumnFilterDatePicker = styled(DatePicker)`
  width: 100%;
  .MuiInputBase-root {
    height: ${FILTER_HEIGHT};
    font-size: ${FILTER_FONT_SIZE};
    background-color: white;
  }
  .MuiInputBase-input {
    padding: 4px 8px;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${Colors.BLACK_10 ?? '#e0e0e0'};
  }
  label:not(.MuiInputLabel-shrink) {
    top: -10px;
    font-size: ${FILTER_FONT_SIZE};
  }
`;

export const ColumnFilterSelect = styled(Select)`
  width: 100%;
  max-width: 100%;
  height: ${FILTER_HEIGHT};
  font-size: ${FILTER_FONT_SIZE};
  background-color: white;
  .MuiSelect-select {
    padding: 4px 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${Colors.BLACK_10 ?? '#e0e0e0'};
  }
`;

export const ColumnFilterAutoComplete = styled(StyledSelect)`
  width: 100%;
  margin-top: 0;
  & .AssetFieldSelect__control {
    height: ${FILTER_HEIGHT};
    min-height: ${FILTER_HEIGHT};
    border: 1px solid ${Colors.BLACK_10 ?? '#e0e0e0'} !important;
    border-radius: 4px;
    font-size: ${FILTER_FONT_SIZE};
    background-color: white;
    .AssetFieldSelect__value-container {
      padding: 0 8px;
      height: ${FILTER_HEIGHT};
      font-size: ${FILTER_FONT_SIZE};
    }
    .AssetFieldSelect__single-value, .AssetFieldSelect__input-container {
      height: 18px;
      margin: 0;
      padding: 0;
      font-size: ${FILTER_FONT_SIZE};
    }
    .AssetFieldSelect__indicators {
      height: ${FILTER_HEIGHT};
    }
  }
  & .AssetFieldSelect__control:hover {
    border-color: ${Colors.BLACK_30 ?? '#999'} !important;
  }
`;

export const ClearFiltersButton = styled(VaultButton)`
  font-size: 12px;
  padding: 4px 4px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
  text-align: center;
  min-width: 0;
`;
